// ════════════════════════════════════════════════════════════
// Merlin UI primitives
// See DESIGN_SYSTEM.md for usage rules.
// ════════════════════════════════════════════════════════════
import { useState, useMemo } from 'react'
import { TIPS } from '../utils/config'
import merlinImg from '../assets/merlin.png'
import merlinSearching from '../assets/Merlin_sort-removebg-preview.png'

// ════════════════════════════════════════════
// MERLIN — mascotte avec bulle de dialogue
// ════════════════════════════════════════════
export function Merlin({ children, className = '' }) {
  return (
    <div className={`genius-wrap ${className}`}>
      <img src={merlinImg} alt="Merlin" className="w-8 h-8 object-contain flex-shrink-0"
        style={{ filter: 'drop-shadow(0 2px 8px rgba(254,130,4,0.15))' }} />
      {children && (
        <div className="genius-bubble">
          {typeof children === 'string' ? <p>{children}</p> : children}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════
// LOADER — Merlin qui lance un sort
// ════════════════════════════════════════════
const LOADING_MESSAGES = [
  "Merlin consulte sa boule de cristal...",
  "Merlin fouille dans ses grimoires...",
  "Merlin prepare ses sortileges...",
  "Merlin analyse les etoiles...",
  "Merlin invoque les donnees...",
];

export function Loader({ text }) {
  const msg = text || LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];
  return (
    <div className="flex flex-col items-center justify-center py-20 animate-in">
      <div className="relative mb-4">
        <div className="absolute inset-0 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(254,130,4,0.12) 0%, transparent 70%)', filter: 'blur(20px)', transform: 'scale(2.5)' }} />
        <img src={merlinSearching} alt="Merlin" className="w-24 h-24 object-contain animate-bounce-slow relative z-10"
          style={{ filter: 'drop-shadow(0 4px 16px rgba(254,130,4,0.2))' }} />
      </div>
      <p className="text-ink-muted text-base font-medium">{msg}</p>
      <div className="mt-3 flex gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
        <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" style={{ animationDelay: '0.2s' }} />
        <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" style={{ animationDelay: '0.4s' }} />
      </div>
    </div>
  )
}

// ════════════════════════════════════════════
// SPINNER — compact inline loader
// Usage: <Spinner size="xs|sm|md|lg" color="orange|purple|white|ink" />
// ════════════════════════════════════════════
const SPINNER_SIZES = {
  xs: 'w-3 h-3 border',
  sm: 'w-4 h-4 border-2',
  md: 'w-6 h-6 border-2',
  lg: 'w-10 h-10 border-[3px]',
}
const SPINNER_COLORS = {
  orange: 'border-orange/30 border-t-orange',
  purple: 'border-purple-400/30 border-t-purple-500',
  white:  'border-white/25 border-t-white',
  ink:    'border-ink/20 border-t-ink',
}
export function Spinner({ size = 'sm', color = 'orange', className = '' }) {
  return (
    <div className={`${SPINNER_SIZES[size] || SPINNER_SIZES.sm} ${SPINNER_COLORS[color] || SPINNER_COLORS.orange} rounded-full animate-spin flex-shrink-0 ${className}`} aria-label="Chargement" />
  )
}

// ════════════════════════════════════════════
// EMPTY STATE — Merlin mascot + message + CTA
// Usage: <EmptyState title="..." description="..." cta={<Button>...</Button>} mascot="idea|searching|success" />
// ════════════════════════════════════════════
export function EmptyState({ title, description, cta, mascot = 'idea', className = '' }) {
  const mascots = {
    idea: merlinSearching, // fallback to searching for now
    searching: merlinSearching,
    success: merlinSearching,
  }
  return (
    <div className={`relative text-center py-14 px-6 rounded-2xl overflow-hidden ${className}`}
      style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.04), rgba(254,130,4,0.03))', border: '1px solid rgba(124,58,237,0.08)' }}>
      <Sparkles count={10} color="#fe8204" className="opacity-40" />
      <div className="relative z-10">
        <img src={mascots[mascot] || mascots.idea} alt="Merlin" className="w-24 h-24 mx-auto object-contain mb-3 merlin-float"
          style={{ filter: 'drop-shadow(0 8px 24px rgba(254,130,4,0.25))' }} />
        {title && <p className="text-base font-bold mb-1" style={{ color: '#1a1040' }}>{title}</p>}
        {description && (
          <p className="text-sm max-w-xs mx-auto leading-relaxed mb-4" style={{ color: 'rgba(139,92,246,0.7)' }}>
            {description}
          </p>
        )}
        {cta}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════
// BUTTON — 4 variantes × 4 tailles
// Usage: <Button variant="primary" size="md" loading={bool} disabled={bool}>Label</Button>
// ════════════════════════════════════════════
const BTN_VARIANTS = {
  primary:   'text-white shadow-sm hover:shadow-glow-primary hover:scale-[1.02]',
  secondary: 'bg-white text-ink-900 border border-border hover:border-ink-400 hover:bg-surface-2',
  ghost:     'text-ink-500 hover:text-ink-900 hover:bg-black/5',
  danger:    'text-white bg-danger-500 hover:bg-danger-500/90 shadow-sm',
  night:     'text-white backdrop-blur-sm',
}
const BTN_VARIANT_STYLES = {
  primary: { background: 'linear-gradient(135deg, #fe8204 0%, #f59e0b 100%)' },
  night:   { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' },
}
const BTN_SIZES = {
  xs: 'px-2.5 py-1 text-xs font-semibold',
  sm: 'px-3.5 py-1.5 text-sm font-semibold',
  md: 'px-5 py-2.5 text-sm font-bold',
  lg: 'px-6 py-3 text-base font-bold',
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon = null,
  iconRight = null,
  className = '',
  ...rest
}) {
  const isDisabled = disabled || loading
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      style={BTN_VARIANT_STYLES[variant]}
      className={[
        'inline-flex items-center justify-center gap-2 rounded-xl transition-all duration-fast ease-out-expo',
        'active:scale-[0.97] select-none',
        BTN_VARIANTS[variant] || BTN_VARIANTS.primary,
        BTN_SIZES[size] || BTN_SIZES.md,
        fullWidth ? 'w-full' : '',
        isDisabled ? 'opacity-40 pointer-events-none' : 'cursor-pointer',
        className,
      ].join(' ')}
      {...rest}
    >
      {loading ? (
        <span className="w-3.5 h-3.5 border-2 border-current/25 border-t-current rounded-full animate-spin" />
      ) : icon}
      {children}
      {iconRight}
    </button>
  )
}

// Backwards-compat: Btn alias
export function Btn(props) { return <Button {...props} /> }

// ════════════════════════════════════════════
// BADGE — pills sémantiques
// Variants: primary, secondary, success, warning, danger, neutral, premium, ghost
// ════════════════════════════════════════════
const BADGE_VARIANTS = {
  primary:   { bg: 'rgba(254,130,4,0.10)',   fg: '#c2410c', border: 'rgba(254,130,4,0.25)' },
  secondary: { bg: 'rgba(124,58,237,0.10)',  fg: '#5b21b6', border: 'rgba(124,58,237,0.25)' },
  success:   { bg: 'rgba(22,163,74,0.10)',   fg: '#15803d', border: 'rgba(22,163,74,0.25)' },
  warning:   { bg: 'rgba(245,158,11,0.10)',  fg: '#b45309', border: 'rgba(245,158,11,0.25)' },
  danger:    { bg: 'rgba(220,38,38,0.10)',   fg: '#b91c1c', border: 'rgba(220,38,38,0.25)' },
  neutral:   { bg: 'rgba(26,16,64,0.06)',    fg: '#2d1b4e', border: 'rgba(26,16,64,0.10)' },
  ghost:     { bg: 'transparent',            fg: '#8a7ea8', border: 'rgba(26,16,64,0.12)' },
  premium:   { bg: 'linear-gradient(135deg, #fe8204, #f59e0b)', fg: 'white', border: 'transparent' },
}
const BADGE_SIZES = {
  xs: 'text-xs px-1.5 py-0.5',
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
}

export function Badge({ children, variant = 'neutral', size = 'sm', icon = null, className = '' }) {
  const v = BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-semibold ${BADGE_SIZES[size] || BADGE_SIZES.sm} ${className}`}
      style={{ background: v.bg, color: v.fg, border: `1px solid ${v.border}` }}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </span>
  )
}

// Legacy: Tag — maps old API to new Badge
export function Tag({ children, variant = 'default', className = '' }) {
  const map = { default: 'neutral', orange: 'primary', blue: 'secondary', ink: 'neutral', green: 'success', ghost: 'ghost', crossniche: 'secondary' }
  return <Badge variant={map[variant] || 'neutral'} className={className}>{children}</Badge>
}

// ════════════════════════════════════════════
// CARD — container sémantique
// Theme: day (default), night, glass, outlined
// ════════════════════════════════════════════
const CARD_THEMES = {
  day:      'bg-white border border-border shadow-sm',
  night:    'text-white border border-white/10 shadow-lg',
  glass:    'backdrop-blur-md border border-white/10 shadow-md',
  outlined: 'bg-white border-2 border-border shadow-none',
  'day-elevated': 'bg-white border border-border shadow-md',
}
const CARD_THEME_STYLES = {
  night: { background: 'linear-gradient(135deg, #1e1145 0%, #2d1b69 40%, #3b1f8e 100%)' },
  glass: { background: 'rgba(255,255,255,0.06)' },
}

export function Card({ children, theme = 'day', padding = 'md', className = '', style = {}, ...rest }) {
  const pad = { none: '', xs: 'p-2', sm: 'p-3', md: 'p-5', lg: 'p-6', xl: 'p-8' }[padding] || 'p-5'
  return (
    <div
      className={`rounded-xl ${CARD_THEMES[theme] || CARD_THEMES.day} ${pad} ${className}`}
      style={{ ...(CARD_THEME_STYLES[theme] || {}), ...style }}
      {...rest}
    >
      {children}
    </div>
  )
}

// ════════════════════════════════════════════
// SECTION — conteneur de page avec header
// ════════════════════════════════════════════
export function Section({ title, subtitle, icon, action, children, className = '' }) {
  return (
    <section className={`mb-6 ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            {icon && <span className="text-lg flex-shrink-0" aria-hidden>{icon}</span>}
            <div className="min-w-0">
              {title && <h2 className="text-lg font-bold text-ink-900 leading-tight truncate">{title}</h2>}
              {subtitle && <p className="text-sm text-ink-500 leading-tight mt-0.5 truncate">{subtitle}</p>}
            </div>
          </div>
          {action && <div className="flex-shrink-0 ml-3">{action}</div>}
        </div>
      )}
      {children}
    </section>
  )
}

// ════════════════════════════════════════════
// SCORE — notation unifiée /10
// variant: badge | stars | bar
// ════════════════════════════════════════════
export const SCORE_COLORS = {
  excellent: { fg: '#16a34a', bg: 'rgba(22,163,74,0.10)', label: 'Excellent' },
  good:      { fg: '#f59e0b', bg: 'rgba(245,158,11,0.10)', label: 'Bon' },
  weak:      { fg: '#f97316', bg: 'rgba(249,115,22,0.12)', label: 'Faible' },
  critical:  { fg: '#dc2626', bg: 'rgba(220,38,38,0.10)', label: 'Critique' },
}
export function getScoreLevel(score) {
  if (score == null) return null
  if (score >= 8) return 'excellent'
  if (score >= 6) return 'good'
  if (score >= 4) return 'weak'
  return 'critical'
}
export function getScoreColor(score) {
  const level = getScoreLevel(score)
  return level ? SCORE_COLORS[level] : null
}

export function Score({ value, max = 10, variant = 'badge', showLabel = false, size = 'md' }) {
  if (value == null) return null
  const lvl = getScoreLevel(value)
  const c = SCORE_COLORS[lvl]

  if (variant === 'stars') {
    const starCount = 5
    // Keep fractional precision: 5/10 → 2.5/5 rendered as 2 full + 1 half star (not rounded to 3)
    const filledPct = Math.min(100, Math.max(0, (value / max) * 100))
    const starSize = { xs: 10, sm: 12, md: 14, lg: 18 }[size] || 14
    const renderStars = (color) => Array.from({ length: starCount }).map((_, i) => (
      <span key={i} style={{ fontSize: starSize, color, lineHeight: 1 }}>★</span>
    ))
    return (
      <span className="relative inline-flex items-center" aria-label={`${value}/${max}`} title={`${value}/${max}`}>
        <span className="inline-flex items-center gap-px">
          {renderStars('rgba(26,16,64,0.12)')}
        </span>
        <span className="absolute inset-0 inline-flex items-center gap-px overflow-hidden pointer-events-none"
          style={{ width: `${filledPct}%` }}>
          {renderStars('#f59e0b')}
        </span>
      </span>
    )
  }

  if (variant === 'bar') {
    const pct = Math.min(100, Math.round((value / max) * 100))
    return (
      <div className="w-full">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-ink-500 font-semibold">{showLabel ? c.label : ''}</span>
          <span className="font-mono font-bold" style={{ color: c.fg }}>{value.toFixed(1)}/{max}</span>
        </div>
        <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(26,16,64,0.06)' }}>
          <div className="h-full rounded-full transition-all duration-slow" style={{ width: `${pct}%`, background: c.fg }} />
        </div>
      </div>
    )
  }

  // default: badge
  const sz = { xs: 'text-xs px-1.5 py-0.5', sm: 'text-xs px-2 py-0.5', md: 'text-sm px-2.5 py-1', lg: 'text-base px-3 py-1' }[size] || 'text-sm px-2.5 py-1'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-bold ${sz}`}
      style={{ color: c.fg, background: c.bg, border: `1px solid ${c.fg}30` }}>
      {value.toFixed(1)}/{max}
      {showLabel && <span className="opacity-70 text-xs font-semibold uppercase tracking-wider ml-0.5">{c.label}</span>}
    </span>
  )
}

// Legacy: Stars — maps to Score variant="stars"
export function Stars({ score, max = 5, size = 12, color = '#f59e0b', emptyColor = 'rgba(0,0,0,0.1)' }) {
  if (score == null) return null
  const filled = Math.round((score / 10) * max)
  return (
    <span className="inline-flex items-center gap-px">
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ fontSize: size, color: i < filled ? color : emptyColor, transition: 'color 0.3s' }}>★</span>
      ))}
    </span>
  )
}

// ════════════════════════════════════════════
// INPUT — champ texte unifié (light + night)
// ════════════════════════════════════════════
export function Input({ label, error, helper, theme = 'day', leftIcon, rightIcon, className = '', ...rest }) {
  const baseStyle = theme === 'night'
    ? 'bg-white/5 text-white placeholder-white/35 border border-white/10 focus:border-primary-500 focus:bg-white/10'
    : 'bg-white text-ink-900 placeholder-ink-300 border border-border focus:border-primary-500 focus:bg-white'
  return (
    <label className={`block ${className}`}>
      {label && <span className={`block text-sm font-semibold mb-1.5 ${theme === 'night' ? 'text-white/80' : 'text-ink-700'}`}>{label}</span>}
      <div className="relative">
        {leftIcon && <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme === 'night' ? 'text-white/50' : 'text-ink-400'}`}>{leftIcon}</span>}
        <input
          className={`w-full px-3.5 py-2.5 text-sm rounded-xl outline-none transition-all duration-fast ${baseStyle} ${leftIcon ? 'pl-10' : ''} ${rightIcon ? 'pr-10' : ''}`}
          {...rest}
        />
        {rightIcon && <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme === 'night' ? 'text-white/50' : 'text-ink-400'}`}>{rightIcon}</span>}
      </div>
      {error && <span className="block text-xs text-danger-500 mt-1">{error}</span>}
      {!error && helper && <span className={`block text-xs mt-1 ${theme === 'night' ? 'text-white/40' : 'text-ink-400'}`}>{helper}</span>}
    </label>
  )
}

// ════════════════════════════════════════════
// EXPANDABLE — toggle + contenu
// ════════════════════════════════════════════
export function Expandable({ summary, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-primary-500 transition-colors duration-fast font-medium">
        <span className="transition-transform duration-fast inline-block text-xs" style={{ transform: open ? 'rotate(90deg)' : '' }}>▶</span>
        {summary}
      </button>
      {open && <div className="mt-2 animate-fade">{children}</div>}
    </div>
  )
}

// ════════════════════════════════════════════
// INFO TIP — question mark avec tooltip
// ════════════════════════════════════════════
export function InfoTip({ tipKey, children }) {
  const text = TIPS[tipKey] || children
  return (
    <span className="info-tip">
      <span>?</span>
      <span className="tip-content">{text}</span>
    </span>
  )
}

// ════════════════════════════════════════════
// VIDEO THUMB — lecture au hover, fallback sur erreur
// ════════════════════════════════════════════
export function VideoThumb({ src, className, onClick, fallback }) {
  const [err, setErr] = useState(false)
  if (err || !src) return fallback || null
  return (
    <video muted preload="metadata" playsInline className={className || ''} onClick={onClick}
      style={{ objectFit: 'cover', pointerEvents: onClick ? 'auto' : 'none' }}
      onMouseEnter={e => { try { e.target.play() } catch {} }}
      onMouseLeave={e => { try { e.target.pause(); e.target.currentTime = 0 } catch {} }}
      onError={() => setErr(true)}>
      <source src={src} type="video/mp4" />
    </video>
  )
}

// ════════════════════════════════════════════
// DIVIDER — séparateur
// ════════════════════════════════════════════
export function Divider({ label, className = '', theme = 'day' }) {
  const lineColor = theme === 'night' ? 'rgba(255,255,255,0.12)' : 'rgba(26,16,64,0.08)'
  const labelColor = theme === 'night' ? 'rgba(255,255,255,0.45)' : 'rgba(26,16,64,0.45)'
  if (!label) {
    return <div className={`w-full h-px ${className}`} style={{ background: lineColor }} />
  }
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex-1 h-px" style={{ background: lineColor }} />
      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: labelColor }}>{label}</span>
      <div className="flex-1 h-px" style={{ background: lineColor }} />
    </div>
  )
}

// ════════════════════════════════════════════
// SPARKLES — particules animées pour fonds "magique"
// ════════════════════════════════════════════
export function Sparkles({ count = 12, color = '#fe8204', className = '', inline = false }) {
  // Freeze positions across re-renders — otherwise every parent keystroke re-randomizes the sparkles
  const positions = useMemo(() => Array.from({ length: count }).map(() => ({
    left: `${5 + Math.random() * 90}%`,
    top: `${10 + Math.random() * 80}%`,
    size: 4 + Math.random() * 6,
    delay: Math.random() * 3,
    duration: 2 + Math.random() * 2,
  })), [count])
  return (
    <div className={`${inline ? 'relative' : 'absolute inset-0 overflow-hidden pointer-events-none'} ${className}`} aria-hidden>
      {positions.map((p, i) => (
        <span key={i} className="absolute animate-sparkle"
          style={{
            left: p.left,
            top: p.top,
            fontSize: `${p.size}px`,
            color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            filter: 'drop-shadow(0 0 3px currentColor)',
          }}>
          ✦
        </span>
      ))}
    </div>
  )
}
